/**
 * Title Generator Service
 *
 * Registry: title_generator — inline assist. Transport: callAgentLlm.
 */

import { AgentLlmError, callAgentLlm } from './llmClient';
import { isAiCapabilityEnabled } from './ai/aiAssistSettings';
import type { ItemLanguageType } from '../types/prioritization/lifecycle';
import { DEFAULT_MODEL } from '../../supabase/functions/_shared/modelRequest.ts';

const SYSTEM_PROMPT =
  'You are a precision naming assistant for a strategic prioritization tool. ' +
  'Given an item description, produce a single short, clear title. ' +
  'Respond with the title text only — no quotes, no punctuation at the end, no explanation.';

export interface GenerateTitleInput {
  description: string;
  category?: ItemLanguageType;
  model?: string;
}

/**
 * Generate a concise title for an item from its description.
 * Throws {@link AgentLlmError} if no LLM path is available.
 */
export async function generateItemTitle(input: GenerateTitleInput): Promise<string> {
  const { description, category, model = DEFAULT_MODEL } = input;

  if (!isAiCapabilityEnabled('title_generator')) {
    throw new AgentLlmError('Title generation is disabled for this workspace.');
  }

  if (!description.trim()) {
    throw new AgentLlmError('A description is required to generate a title.');
  }

  const categoryHint = category
    ? `Item type: ${category}.\n`
    : '';

  const userPrompt = `${categoryHint}Description:\n${description.trim()}\n\nWrite a title (8 words or fewer, no trailing punctuation):`;

  const result = await callAgentLlm({
    userPrompt,
    systemPrompt: SYSTEM_PROMPT,
    model,
    conversationId: `title-gen-${Date.now()}`,
    maxTokens: 60,
    temperature: 0.3,
  });

  const title = result.trim().replace(/^["']|["']$/g, '').replace(/[.!?]$/, '').trim();
  if (!title) {
    throw new AgentLlmError('Title generator returned an empty response.');
  }
  return title;
}
